export const SUITS = ['S', 'H', 'D', 'C'] as const;
export type Suit = (typeof SUITS)[number];

export type Rank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
export const RANKS: readonly Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

/** Immutable, unique card identity: `packIndex:suit:rank` (PRD §3 seating notes). */
export type CardId = string;

export interface Card {
  readonly id: CardId;
  readonly pack: number;
  readonly suit: Suit;
  readonly rank: Rank;
}

export const SUIT_NAME: Record<Suit, string> = {
  S: 'Spades',
  H: 'Hearts',
  D: 'Diamonds',
  C: 'Clubs',
};

export const SUIT_SYMBOL: Record<Suit, string> = {
  S: '♠',
  H: '♥',
  D: '♦',
  C: '♣',
};

export const RANK_LABEL: Record<Rank, string> = {
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
};

export function makeCardId(pack: number, suit: Suit, rank: Rank): CardId {
  return `${pack}:${suit}:${rank}`;
}

const cache = new Map<CardId, Card>();

export function parseCard(id: CardId): Card {
  const cached = cache.get(id);
  if (cached) return cached;
  const [packText, suitText, rankText] = id.split(':');
  const pack = Number(packText);
  const rank = Number(rankText) as Rank;
  if (
    !Number.isInteger(pack) ||
    pack < 0 ||
    !SUITS.includes(suitText as Suit) ||
    !RANKS.includes(rank)
  ) {
    throw new Error(`Invalid card id: ${id}`);
  }
  const card: Card = Object.freeze({ id, pack, suit: suitText as Suit, rank });
  cache.set(id, card);
  return card;
}

export function isCardId(value: unknown): value is CardId {
  if (typeof value !== 'string') return false;
  try {
    parseCard(value);
    return true;
  } catch {
    return false;
  }
}

export const suitOf = (id: CardId): Suit => parseCard(id).suit;
export const rankOf = (id: CardId): Rank => parseCard(id).rank;
export const isTen = (id: CardId): boolean => parseCard(id).rank === 10;

export function cardLabel(id: CardId): string {
  const card = parseCard(id);
  return `${RANK_LABEL[card.rank]}${SUIT_SYMBOL[card.suit]}`;
}

/** Display order for a hand: suits alternate colour, ranks high → low. */
const SUIT_DISPLAY_ORDER: Record<Suit, number> = { S: 0, H: 1, C: 2, D: 3 };

export function sortHand(cards: readonly CardId[]): CardId[] {
  return [...cards].sort((a, b) => {
    const ca = parseCard(a);
    const cb = parseCard(b);
    if (ca.suit !== cb.suit) return SUIT_DISPLAY_ORDER[ca.suit] - SUIT_DISPLAY_ORDER[cb.suit];
    if (ca.rank !== cb.rank) return cb.rank - ca.rank;
    return ca.pack - cb.pack;
  });
}
